import { useState, useEffect } from 'react';
import type { HomeworkItem } from './homeworkTypes';
import { fetchClasses, fetchClassSections, fetchClassSubjects, type ClassApiRecord, type SectionApiRecord, type ClassSubjectRecord } from '../../services/classApi';
import { createHomework, fetchHomeworks, type HomeworkApiRecord } from '../../services/homeworkApi';
import './HomeworkLedger.css';
import { useTranslation } from 'react-i18next';

interface HomeworkLedgerProps {
    onEvaluate?: (homework: HomeworkItem) => void;
}

export default function HomeworkLedger({ onEvaluate }: HomeworkLedgerProps) {
    const { t: _t } = useTranslation();
    /* ── Data state ── */
    const [homework, setHomework] = useState<HomeworkItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [successMsg, setSuccessMsg] = useState('');

    /* ── Drawer form state ── */
    const [formTitle, setFormTitle] = useState('');
    const [formDesc, setFormDesc] = useState('');
    const [formClassId, setFormClassId] = useState('');
    const [formSectionId, setFormSectionId] = useState('');
    const [formSubjectId, setFormSubjectId] = useState('');
    const [formDueDate, setFormDueDate] = useState('');
    const [formTotalMarks, setFormTotalMarks] = useState('');
    const [saving, setSaving] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');

    /* ── API dropdown state ── */
    const [classes, setClasses] = useState<ClassApiRecord[]>([]);
    const [sections, setSections] = useState<SectionApiRecord[]>([]);
    const [subjects, setSubjects] = useState<ClassSubjectRecord[]>([]);
    const [dropdownsLoading, setDropdownsLoading] = useState(true);

    /* ── Filter state ── */
    const [filterClassId, setFilterClassId] = useState('');
    const [filterSectionId, setFilterSectionId] = useState('');
    const [filterSections, setFilterSections] = useState<SectionApiRecord[]>([]);

    /* ── Map API record → HomeworkItem ── */
    const mapApiToItem = (r: HomeworkApiRecord): HomeworkItem => ({
        id: r.id,
        title: r.title,
        description: r.description ?? '',
        class_id: r.class_id,
        class_name: r.school_class?.name || '',
        section_id: r.section_id,
        section_name: r.section?.name || null,
        subject_id: r.subject_id,
        subject_name: r.subject?.board_subject_name || '',
        due_date: r.due_date.includes('T') ? r.due_date.split('T')[0] : r.due_date,
        total_marks: r.total_marks != null ? Number(r.total_marks) : null,
        total_assigned: 0,
        submitted: 0,
        evaluated: 0,
        created_at: r.created_at.includes('T') ? r.created_at.split('T')[0] : r.created_at,
    });

    /* ── Load homework with given filters ── */
    const loadHomework = (classId: string, sectionId: string) => {
        setLoading(true);
        return fetchHomeworks({
            perPage: 100,
            classId: classId || undefined,
            sectionId: sectionId && sectionId !== '__all__' ? sectionId : undefined,
        }).then((res) => {
            setHomework(res.data.map(mapApiToItem));
        }).catch(() => {
            setHomework([]);
        }).finally(() => {
            setLoading(false);
        });
    };

    /* ── Load classes + initial homework on mount ── */
    useEffect(() => {
        setDropdownsLoading(true);
        fetchClasses({ perPage: 100 })
            .then((res) => setClasses(res.data))
            .catch(() => { /* silent */ })
            .finally(() => setDropdownsLoading(false));
        loadHomework('', '');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /* ── Filter cascade: sections on filter class change ── */
    useEffect(() => {
        setFilterSections([]);
        setFilterSectionId('');
        if (!filterClassId) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await fetchClassSections(filterClassId, { perPage: 100 });
                if (!cancelled) setFilterSections(res.data);
            } catch {
                if (!cancelled) setFilterSections([]);
            }
        })();
        return () => { cancelled = true; };
    }, [filterClassId]);

    /* ── Cascade: sections on class change ── */
    useEffect(() => {
        setSections([]);
        setFormSectionId('');
        setSubjects([]);
        setFormSubjectId('');
        if (!formClassId) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await fetchClassSections(formClassId, { perPage: 100 });
                if (!cancelled) setSections(res.data);
            } catch {
                if (!cancelled) setSections([]);
            }
        })();
        return () => { cancelled = true; };
    }, [formClassId]);

    /* ── Cascade: subjects on class + section change ── */
    useEffect(() => {
        setSubjects([]);
        setFormSubjectId('');
        if (!formClassId) return;
        let cancelled = false;
        const effectiveSection = formSectionId && formSectionId !== '__all__' ? formSectionId : undefined;
        (async () => {
            try {
                const res = await fetchClassSubjects(formClassId, {
                    perPage: 100,
                    sectionId: effectiveSection,
                });
                if (!cancelled) setSubjects(res.data);
            } catch {
                if (!cancelled) setSubjects([]);
            }
        })();
        return () => { cancelled = true; };
    }, [formClassId, formSectionId]);

    /* ── Open drawer ── */
    const openDrawer = () => {
        setFormTitle('');
        setFormDesc('');
        setFormClassId('');
        setFormSectionId('');
        setFormSubjectId('');
        setFormDueDate('');
        setFormTotalMarks('');
        setErrorMsg('');
        setDrawerOpen(true);
    };

    /* ── Submit homework ── */
    const handleSubmit = async () => {
        if (!formTitle || !formClassId || !formSubjectId || !formDueDate) return;
        setSaving(true);
        setErrorMsg('');

        const sectionOption =
            formSectionId === '__all__' || formSectionId === '' ? null : formSectionId;
        const selectedClass = classes.find((c) => c.id === formClassId);
        const selectedSection = sectionOption
            ? sections.find((s) => s.id === sectionOption)
            : null;
        const selectedSubject = subjects.find((s) => s.school_subject_id === formSubjectId);

        try {
            const apiRecord = await createHomework({
                title: formTitle,
                description: formDesc || null,
                class_id: formClassId,
                section_id: sectionOption,
                school_subject_id: formSubjectId,
                due_date: formDueDate,
                total_marks: formTotalMarks ? Number(formTotalMarks) : null,
            });

            // API may not return nested relations on create — fill from form selections
            const newHw: HomeworkItem = {
                ...mapApiToItem(apiRecord),
                class_name: apiRecord.school_class?.name || selectedClass?.name || '',
                section_name: apiRecord.section?.name || selectedSection?.name || null,
                subject_name: apiRecord.subject?.board_subject_name || selectedSubject?.subject_name || selectedSubject?.board_subject?.name || '',
            };
            setHomework((prev) => [newHw, ...prev]);
            setDrawerOpen(false);
            setSuccessMsg('Homework assigned successfully!');
            setTimeout(() => setSuccessMsg(''), 5000);
        } catch (err: unknown) {
            const apiErr = err as { errors?: Record<string, string[]>; message?: string };
            if (apiErr.errors) {
                const messages = Object.values(apiErr.errors).flat();
                setErrorMsg(messages.join(' '));
            } else {
                setErrorMsg(apiErr.message || 'Failed to create homework. Please try again.');
            }
        } finally {
            setSaving(false);
        }
    };

    const formValid = formTitle && formClassId && formSubjectId && formDueDate;

    /* ── Helpers ── */
    const formatDate = (d: string) => {
        const dateStr = d.includes('T') ? d.split('T')[0] : d;
        const date = new Date(dateStr + 'T00:00:00');
        return date.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        });
    };

    const completionRate = (hw: HomeworkItem) => {
        if (hw.total_assigned === 0) return 0;
        return Math.round(((hw.submitted + hw.evaluated) / hw.total_assigned) * 100);
    };

    return (
        <div className="homework">
            {successMsg && (
                <div className="homework__toast">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
                    <span>{successMsg}</span>
                    <button className="homework__toast-dismiss" onClick={() => setSuccessMsg('')} aria-label="Dismiss">✕</button>
                </div>
            )}
            {errorMsg && (
                <div className="homework__toast homework__toast--error">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></svg>
                    <span>{errorMsg}</span>
                    <button className="homework__toast-dismiss" onClick={() => setErrorMsg('')} aria-label="Dismiss">✕</button>
                </div>
            )}

            {/* Header */}
            <div className="homework__header">
                <div className="homework__header-text">
                    <h1 className="homework__title">Homework</h1>
                    <p className="homework__subtitle">
                        Manage assignments, track submissions, and evaluate student work.
                    </p>
                </div>
                <button className="homework__add-btn" onClick={openDrawer}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                    Assign Homework
                </button>
            </div>

            {/* Filters */}
            <div className="homework__filters">
                <div className="homework__filter-field">
                    <label className="homework__label" htmlFor="hwFilterClass">Class</label>
                    <select
                        className="homework__select"
                        id="hwFilterClass"
                        value={filterClassId}
                        onChange={(e) => setFilterClassId(e.target.value)}
                        disabled={dropdownsLoading}
                    >
                        <option value="">All Classes</option>
                        {classes.map((c) => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                    </select>
                </div>
                <div className="homework__filter-field">
                    <label className="homework__label" htmlFor="hwFilterSection">Section</label>
                    <select
                        className="homework__select"
                        id="hwFilterSection"
                        value={filterSectionId}
                        onChange={(e) => setFilterSectionId(e.target.value)}
                        disabled={!filterClassId || filterSections.length === 0}
                    >
                        <option value="">All Sections</option>
                        {filterSections.map((s) => (
                            <option key={s.id} value={s.id}>{s.name}</option>
                        ))}
                    </select>
                </div>
                <button
                    type="button"
                    className="homework__filter-search"
                    onClick={() => loadHomework(filterClassId, filterSectionId)}
                    disabled={loading}
                >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
                    Search
                </button>
                {(filterClassId || filterSectionId) && (
                    <button
                        type="button"
                        className="homework__filter-clear"
                        onClick={() => { setFilterClassId(''); setFilterSectionId(''); }}
                    >
                        Clear filters
                    </button>
                )}
            </div>

            {/* Grid */}
            {loading ? (
                <div className="homework__loading">
                    <div className="homework__loader">
                        <span className="homework__loader-dot" />
                        <span className="homework__loader-dot" />
                        <span className="homework__loader-dot" />
                    </div>
                </div>
            ) : homework.length === 0 ? (
                <div className="homework__empty-state">
                    <div className="homework__empty-icon">
                        <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /></svg>
                    </div>
                    <h3 className="homework__empty-title">No Homework Assigned</h3>
                    <p className="homework__empty-text">Click "Assign Homework" to create your first assignment.</p>
                </div>
            ) : (
                <div className="homework__grid-wrap">
                    <table className="homework__table">
                        <thead>
                            <tr>
                                <th className="homework__th">Title</th>
                                <th className="homework__th">Subject</th>
                                <th className="homework__th">Target</th>
                                <th className="homework__th">Due Date</th>
                                <th className="homework__th">Completion</th>
                                <th className="homework__th homework__th--actions">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {homework.map((hw) => {
                                const rate = completionRate(hw);
                                return (
                                    <tr className="homework__row" key={hw.id}>
                                        <td className="homework__td homework__td--title">{hw.title}</td>
                                        <td className="homework__td homework__td--subject">{hw.subject_name}</td>
                                        <td className="homework__td">
                                            {hw.section_id === null ? (
                                                <span className="homework__target-badge homework__target-badge--broadcast">
                                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" /><path d="M19.07 4.93a10 10 0 0 1 0 14.14" /><path d="M15.54 8.46a5 5 0 0 1 0 7.07" /></svg>
                                                    {hw.class_name} (All Sections)
                                                </span>
                                            ) : (
                                                <span className="homework__target-badge homework__target-badge--section">
                                                    {hw.class_name} – {hw.section_name}
                                                </span>
                                            )}
                                        </td>
                                        <td className="homework__td homework__td--due">{formatDate(hw.due_date)}</td>
                                        <td className="homework__td">
                                            <div className="homework__completion">
                                                <span className="homework__completion-text">
                                                    {hw.submitted + hw.evaluated} / {hw.total_assigned} ({rate}%)
                                                </span>
                                                <div className="homework__progress-bar">
                                                    <div
                                                        className={`homework__progress-fill ${rate >= 80 ? 'homework__progress-fill--high' : ''}`}
                                                        style={{ width: `${rate}%` }}
                                                    />
                                                </div>
                                            </div>
                                        </td>
                                        <td className="homework__td" style={{ textAlign: 'center' }}>
                                            <button
                                                className="homework__eval-btn"
                                                onClick={() => onEvaluate?.(hw)}
                                            >
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                                                Evaluate
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* ═══ Creation Drawer ═══ */}
            {drawerOpen && (
                <>
                    <div className="homework__drawer-overlay" onClick={() => setDrawerOpen(false)} />
                    <div className="homework__drawer">
                        <div className="homework__drawer-header">
                            <h2 className="homework__drawer-title">Assign Homework</h2>
                            <button className="homework__drawer-close" onClick={() => setDrawerOpen(false)}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                            </button>
                        </div>

                        <div className="homework__drawer-body">
                            {/* ─ Target Audience ─ */}
                            <div className="homework__drawer-card">
                                <span className="homework__drawer-card-title">Target Audience</span>

                                <div className="homework__field-row">
                                    <div className="homework__field">
                                        <label className="homework__label" htmlFor="hwClass">Class *</label>
                                        <select
                                            className="homework__select"
                                            id="hwClass"
                                            value={formClassId}
                                            onChange={(e) => setFormClassId(e.target.value)}
                                            disabled={dropdownsLoading}
                                        >
                                            <option value="">Select Class…</option>
                                            {classes.map((c) => (
                                                <option key={c.id} value={c.id}>{c.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="homework__field">
                                        <label className="homework__label" htmlFor="hwSection">Section</label>
                                        <select
                                            className="homework__select"
                                            id="hwSection"
                                            value={formSectionId}
                                            onChange={(e) => setFormSectionId(e.target.value)}
                                            disabled={!formClassId || sections.length === 0}
                                        >
                                            <option value="">Select Section…</option>
                                            <option value="__all__">All Sections</option>
                                            {sections.map((s) => (
                                                <option key={s.id} value={s.id}>{s.name}</option>
                                            ))}
                                        </select>
                                        {formSectionId === '__all__' && (
                                            <span className="homework__broadcast-hint">
                                                This homework will be assigned to all sections in the selected class.
                                            </span>
                                        )}
                                    </div>
                                </div>

                                <div className="homework__field">
                                    <label className="homework__label" htmlFor="hwSubject">Subject *</label>
                                    <select
                                        className="homework__select"
                                        id="hwSubject"
                                        value={formSubjectId}
                                        onChange={(e) => setFormSubjectId(e.target.value)}
                                        disabled={!formClassId}
                                    >
                                        <option value="">Select Subject…</option>
                                        {subjects.map((s) => (
                                            <option key={s.id} value={s.school_subject_id}>{s.subject_name || s.board_subject?.name || '—'}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* ─ Assignment Details ─ */}
                            <div className="homework__drawer-card">
                                <span className="homework__drawer-card-title">Assignment Details</span>

                                <div className="homework__field">
                                    <label className="homework__label" htmlFor="hwTitle">Title *</label>
                                    <input
                                        className="homework__input"
                                        id="hwTitle"
                                        type="text"
                                        placeholder="e.g. Chapter 5 Practice Problems"
                                        maxLength={255}
                                        value={formTitle}
                                        onChange={(e) => setFormTitle(e.target.value)}
                                    />
                                </div>

                                <div className="homework__field">
                                    <label className="homework__label" htmlFor="hwDesc">Description</label>
                                    <textarea
                                        className="homework__textarea"
                                        id="hwDesc"
                                        placeholder="Instructions for students…"
                                        value={formDesc}
                                        onChange={(e) => setFormDesc(e.target.value)}
                                    />
                                </div>

                                <div className="homework__field-row">
                                    <div className="homework__field">
                                        <label className="homework__label" htmlFor="hwDue">Due Date *</label>
                                        <input
                                            className="homework__input"
                                            id="hwDue"
                                            type="date"
                                            value={formDueDate}
                                            min={new Date().toISOString().split('T')[0]}
                                            onChange={(e) => setFormDueDate(e.target.value)}
                                        />
                                    </div>
                                    <div className="homework__field">
                                        <label className="homework__label" htmlFor="hwMarks">Total Marks</label>
                                        <input
                                            className="homework__input"
                                            id="hwMarks"
                                            type="number"
                                            placeholder="e.g. 50"
                                            min={0}
                                            value={formTotalMarks}
                                            onChange={(e) => setFormTotalMarks(e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Sticky footer */}
                        <div className="homework__drawer-footer">
                            <button className="homework__drawer-cancel" onClick={() => setDrawerOpen(false)}>
                                Cancel
                            </button>
                            <button
                                className="homework__drawer-save"
                                onClick={handleSubmit}
                                disabled={!formValid || saving}
                            >
                                {saving ? 'Saving…' : 'Assign Homework'}
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
